import get from 'lodash/get';

import { IPublicConfig } from '@r-client/shared/util/core';

import { Reporter } from './reporter';

export type IgnoreFilter = NonNullable<Reporter.Configuration['checkIgnore']>;
type IgnoredMessage = string | RegExp;

const LOCALHOST_URLS = ['http://localhost', 'https://localhost'];

export const HOST_BLOCK_LIST = [
  'file:///',
  'chrome-extension://',
  'https://analytics.tiktok.com',
  ...LOCALHOST_URLS,
];

const URL_BLOCK_LIST = ['file:///', ...LOCALHOST_URLS];

const FILENAME_BLOCK_LIST = [
  'SyntaxError: Unexpected token',
  '(unknown)',
  'https://s.pinimg.com',
  'https://www.youtube.com/s/player/',
];

// Errors that originate outside our application code: browser extensions,
// 3p script injection, iOS WebView bridge races, cross-origin iframes.
// They are not actionable client-side bugs and have been dominating the
// Rollbar inbox.
const MESSAGE_BLOCK_LIST_THIRD_PARTY: IgnoredMessage[] = [
  // Extension running eval() against the page.
  // https://rollbar.com/Republic/r-client-republic/items/1545/
  /call to eval\(\) blocked by CSP/,
  // Extension injection — global `output` not defined in page scope.
  // https://rollbar.com/Republic/r-client-republic/items/1838/
  /Can't find variable:? output/,
  // Extensions hitting the browser.storage quota.
  // https://rollbar.com/Republic/r-client-republic/items/3869/
  /browser\.storage\.local\.set\(\)\. Exceeded storage quota/,
  // Extension injecting a malformed script tag.
  // https://rollbar.com/Republic/r-client-republic/items/474/
  /Unexpected identifier 'https'/,
  // Browser extension port closed before response — chrome.runtime API noise.
  // https://rollbar.com/Republic/r-client-republic/items/4531/
  /Could not establish connection\. Receiving end does not exist/,
  // 3p iframe (cross-origin) trying to dispatch into ours.
  // https://rollbar.com/Republic/r-client-republic/items/6237/
  /Permission denied to access property .* on cross-origin object/,
  // iOS WebView bridge race — page renders before the native bridge is wired.
  // https://rollbar.com/Republic/r-client-republic/items/6970/
  /top\.webkit\.messageHandlers/,
  // Same iOS WebView race, earlier in the bridge bootstrap.
  // https://rollbar.com/Republic/r-client-republic/items/7026/
  /Cannot read property of undefined\/null expression window\.webkit/,
  // NOTE: generic JS parse errors (e.g. `SyntaxError: Unterminated string`)
  // are intentionally not blocked by message — they also fire when our own
  // bundle is truncated, and we don't want to hide those. 3p-injected variants
  // are filtered by source instead (isIgnoredByHost / isIgnoredByFilename).
  // Bad cookie / localStorage parse from injected code (not our JSON).
  // https://rollbar.com/Republic/r-client-republic/items/7064/
  /Expected property name or '\}' in JSON at position 1/,
];

const MESSAGE_BLOCK_LIST_ANY_ENV: IgnoredMessage[] = [
  /^GraphQL.+Operation (\w)+ failed with You are not (authorized|allowed) to perform this action/,
  ...MESSAGE_BLOCK_LIST_THIRD_PARTY,
];

// Expected backend rejections — authorization refusals and not-found domain
// errors. They are not frontend bugs, just business-logic outcomes that the
// UI already handles. Keep them in Rollbar at warning level (still queryable)
// so they stop polluting the error inbox. Real backend NPEs (e.g.
// `undefined method ... for nil`) are intentionally not matched here.
const DOWNGRADE_TO_WARNING_PATTERNS: RegExp[] = [
  // Backend "resource is protected" authz refusals.
  // https://rollbar.com/Republic/r-client-republic/items/3189/ (CreateDocument)
  // https://rollbar.com/Republic/r-client-republic/items/7001/ (CryptoCreateWalletDevice)
  /^GraphQL.+Operation \w+ failed with \w+ is protected/,
  // Backend domain not-found responses for expected lookups.
  // https://rollbar.com/Republic/r-client-republic/items/6515/
  // https://rollbar.com/Republic/r-client-republic/items/6516/
  /^GraphQL.+Operation \w+ failed with IssuerProfile not found/,
];

export const shouldDowngradeToWarning = (message: string): boolean => {
  return DOWNGRADE_TO_WARNING_PATTERNS.some((pattern) => pattern.test(message));
};

const MESSAGE_BLOCK_LIST_PRODUCTION: IgnoredMessage[] = [
  ...MESSAGE_BLOCK_LIST_ANY_ENV,
  /^Minified React error #418[^0-9].*/, // https://reactjs.org/docs/error-decoder.html?invariant=418
  /^Minified React error #423[^0-9].*/, // https://reactjs.org/docs/error-decoder.html?invariant=423
  /^Minified React error #425[^0-9].*/, // https://reactjs.org/docs/error-decoder.html?invariant=425
  // This is quite rare and is hard to debug, might happen when the user has connection issues
  // or just slow connection, reloads the page or closes the tab before the request is finished.
  /^The user aborted a request\./,
  /^Error: Invariant: attempted to hard navigate to the same URL.*/,
  /^TypeError: Failed to fetch/,
];
const MESSAGE_BLOCK_LIST_STAGING: IgnoredMessage[] = [
  ...MESSAGE_BLOCK_LIST_ANY_ENV,
];

export const getIgnoredMessages = (
  environment: IPublicConfig['applicationEnvironment']
): IgnoredMessage[] => {
  return environment === 'production'
    ? MESSAGE_BLOCK_LIST_PRODUCTION
    : MESSAGE_BLOCK_LIST_STAGING;
};

export const isIgnoredByHost: IgnoreFilter = (_isUncaught, _args, item) => {
  try {
    const url = get(item, 'request.url', '') as string;
    if (!url) return false;
    return URL_BLOCK_LIST.some((blockedHost) => url.startsWith(blockedHost));
  } catch {
    return false;
  }
};

type Frame = {
  filename?: string;
};

export const isIgnoredByFilename: IgnoreFilter = (_isUncaught, _args, item) => {
  try {
    const frames = get(item, 'body.trace.frames', []) as Frame[];
    return frames.some(
      ({ filename }) =>
        filename &&
        FILENAME_BLOCK_LIST.some((blockedFile) =>
          filename.startsWith(blockedFile)
        )
    );
  } catch {
    return false;
  }
};
